import { supabase } from '@xanectech/supabase-client';
import type {
  EmailInvitation,
  ContractInvitationData,
  EmailSendResult,
} from '@xanectech/types';
import { createLogger } from '@xanectech/lib';
import { getErrorMessage } from '@xanectech/lib';
import { createContractEmailInvitation } from './ContractSubmissionService';

const logger = createLogger('Data - EmailService');

// Email service types
export interface EmailRecipient {
  email: string;
  name?: string;
}

export interface EmailTemplateVariables {
  contract_name: string;
  company_name: string;
  public_url: string;
  contract_link?: string;
  recipient_name?: string;
  [key: string]: string | undefined;
}

export interface SendEmailRequest {
  to: EmailRecipient[];
  subject: string;
  text?: string;
  html?: string;
  from: string;
  reply_to?: string;
}

export interface ResendApiResponse {
  id: string;
  from: string;
  to: string[];
  created_at: string;
}

interface SendSmtpEmailResponse {
  ok?: boolean;
  id?: string;
  error?: string;
  runtime?: {
    version?: string;
    fingerprint?: string;
  };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toSendSmtpEmailResponse(data: unknown): SendSmtpEmailResponse {
  if (!isObject(data)) {
    return {};
  }

  const runtimeValue = data.runtime;
  const runtime = isObject(runtimeValue)
    ? {
        version:
          typeof runtimeValue.version === 'string'
            ? runtimeValue.version
            : undefined,
        fingerprint:
          typeof runtimeValue.fingerprint === 'string'
            ? runtimeValue.fingerprint
            : undefined,
      }
    : undefined;

  return {
    ok: typeof data.ok === 'boolean' ? data.ok : undefined,
    id: typeof data.id === 'string' ? data.id : undefined,
    error: typeof data.error === 'string' ? data.error : undefined,
    runtime,
  };
}

async function readInvokeErrorContext(error: unknown): Promise<{
  status?: number;
  body?: string;
}> {
  if (!isObject(error) || !(error.context instanceof Response)) {
    return {};
  }

  const response = error.context;

  try {
    return {
      status: response.status,
      body: await response.clone().text(),
    };
  } catch {
    return {
      status: response.status,
    };
  }
}

function parseInvokeErrorBody(body: string | undefined): SendSmtpEmailResponse {
  if (!body) {
    return {};
  }

  try {
    return toSendSmtpEmailResponse(JSON.parse(body));
  } catch {
    return {};
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * EmailService - SMTP email service via Supabase Edge Function
 *
 * Handles contract invitation emails with template variable substitution
 * and robust error handling.
 *
 * Features:
 * - Template variable substitution
 * - Batch email sending with individual error tracking
 * - HTML and plain text email generation
 * - Comprehensive error handling
 * - Type-safe API interactions
 */
export class EmailService {
  constructor() {
    // No longer need to store API key on frontend
  }

  /**
   * Replace template variables in email content
   */
  private substituteTemplateVariables(
    content: string,
    variables: EmailTemplateVariables,
  ): string {
    let result = content;

    // Replace all template variables
    Object.entries(variables as Record<string, string | undefined>).forEach(
      ([key, value]) => {
        // Always process variables, even if empty (to replace with fallback or empty string)
        const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
        const replacementValue = value || `[${key} not provided]`;
        result = result.replace(regex, replacementValue);
      },
    );
    return result;
  }

  /**
   * Generate HTML email content from plain text with basic formatting
   */
  private generateHtmlContent(textContent: string): string {
    // Convert plain text to basic HTML with proper formatting
    const htmlContent = textContent
      .replace(/\n\n/g, '</p><p>') // Double line breaks become paragraph breaks
      .replace(/\n/g, '<br>') // Single line breaks become <br>
      .replace(/^/, '<p>') // Start with paragraph tag
      .replace(/$/, '</p>'); // End with paragraph tag

    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Contract Invitation</title>
    <style>
        body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; 
            line-height: 1.6; 
            color: #333; 
            max-width: 600px; 
            margin: 0 auto; 
            padding: 20px; 
        }
        .header { 
            background-color: #f8f9fa; 
            padding: 20px; 
            border-radius: 8px; 
            margin-bottom: 20px; 
        }
        .content { 
            padding: 20px 0; 
        }
        .footer { 
            font-size: 14px; 
            color: #666; 
            margin-top: 30px; 
            padding-top: 20px; 
            border-top: 1px solid #eee; 
        }
        a { 
            color: #0066cc; 
            text-decoration: none; 
        }
        a:hover { 
            text-decoration: underline; 
        }
        .button {
            display: inline-block;
            background-color: #0066cc;
            color: white;
            padding: 12px 24px;
            border-radius: 6px;
            text-decoration: none;
            margin: 20px 0;
        }
    </style>
</head>
<body>
    <div class="header">
        <h2>Contract Information Request</h2>
    </div>
    <div class="content">
        ${htmlContent}
    </div>
    <div class="footer">
        <p>This email was sent by an automated system. If you have any questions, please contact <a href="mailto:support@secureflow.com">SecureFlow Financial Services</a>.</p>
        <p><small>Powered by TrueID.Locker - Secure Information Management</small></p>
    </div>
</body>
</html>`.trim();
  }

  /**
   * Create default invitation message template
   */
  private getDefaultInvitationTemplate(): string {
    return `Hello,

You have been invited to submit your information for the contract: {{contract_name}}

Please click the link below to access the secure form and provide the requested information:
{{public_url}}

This secure form is provided by {{company_name}} to collect the necessary information for this contract.

If you have any questions or need assistance, please reply to this email.

Thank you for your cooperation.

Best regards,
{{company_name}}`;
  }

  /**
   * Send a single email via SMTP edge function
   */
  private async sendSingleEmail(
    recipient: EmailRecipient,
    subject: string,
    content: string,
    variables: EmailTemplateVariables,
    fromEmail: string,
    replyToEmail?: string,
  ): Promise<EmailSendResult> {
    try {
      // Substitute template variables
      const personalizedSubject = this.substituteTemplateVariables(
        subject,
        variables,
      );
      const personalizedContent = this.substituteTemplateVariables(
        content,
        variables,
      );

      // Generate HTML version
      const htmlContent = this.generateHtmlContent(personalizedContent);

      // Prepare request payload
      const emailRequest: SendEmailRequest = {
        to: [recipient],
        subject: personalizedSubject,
        text: personalizedContent,
        html: htmlContent,
        from: fromEmail,
        reply_to: replyToEmail,
      };

      // Send via Supabase Edge Function
      const invokeResult = await supabase.functions.invoke('send-smtp-email', {
        body: {
          to: emailRequest.to.map((recipient) => recipient.email),
          subject: emailRequest.subject,
          text: emailRequest.text,
          html: emailRequest.html,
          from: emailRequest.from,
        },
      });

      const edgeResponse = toSendSmtpEmailResponse(invokeResult.data);

      if (invokeResult.error) {
        const invokeContext = await readInvokeErrorContext(invokeResult.error);
        const invokeContextEdgeResponse = parseInvokeErrorBody(
          invokeContext.body,
        );
        const invokeErrorMessage = getErrorMessage(
          invokeResult.error,
          'Unknown error invoking email function',
        );
        logger.error('Error invoking send-smtp-email function:', {
          error: invokeErrorMessage,
          status: invokeContext.status,
          edgeErrorBody: invokeContext.body,
          runtimeVersion: invokeContextEdgeResponse.runtime?.version,
          runtimeFingerprint: invokeContextEdgeResponse.runtime?.fingerprint,
          to: recipient.email,
          subject: personalizedSubject,
          from: fromEmail,
        });

        throw new Error(
          `Email sending failed (status: ${invokeContext.status ?? 'unknown'}, runtime: ${invokeContextEdgeResponse.runtime?.fingerprint ?? 'unknown'})`,
          {
            cause: new Error(
              getErrorMessage(
                invokeResult.error,
                'Unknown error invoking email function',
              ),
            ),
          },
        );
      }

      if (!edgeResponse.ok) {
        logger.error('send-smtp-email returned non-ok response', {
          to: recipient.email,
          subject: personalizedSubject,
          from: fromEmail,
          edgeError: edgeResponse.error,
          runtimeVersion: edgeResponse.runtime?.version,
          runtimeFingerprint: edgeResponse.runtime?.fingerprint,
        });
        throw new Error(
          `Email sending failed: ${edgeResponse.error || 'Unknown error'} (runtime: ${edgeResponse.runtime?.fingerprint ?? 'unknown'})`,
          {
            cause: new Error(
              getErrorMessage(edgeResponse.error, 'Unknown error'),
            ),
          },
        );
      }

      // Create a mock response compatible with existing code
      const validatedResponse = {
        id: edgeResponse.id || `mock-${Date.now()}`,
      };

      return {
        success: true,
        resend_id: validatedResponse.id,
      };
    } catch (error) {
      logger.error('Failed to send email:', {
        error: getErrorMessage(error, 'Unknown error'),
      });

      return {
        success: false,
        error: getErrorMessage(error, 'Unknown error'),
      };
    }
  }

  /**
   * Send contract invitations to multiple recipients
   *
   * @param invitationData - Contract and invitation details
   * @returns Updated invitations with send results
   */
  async sendContractInvitations(
    invitationData: ContractInvitationData,
  ): Promise<EmailInvitation[]> {
    const {
      contractId,
      contractName,
      companyName,
      publicUrl,
      fromEmail,
      replyToEmail,
      invitations,
    } = invitationData;

    // Prepare template variables
    const templateVariables: EmailTemplateVariables = {
      contract_name: contractName,
      company_name: companyName,
      public_url: publicUrl,
      contract_link: `<a href="${escapeHtml(publicUrl)}">${escapeHtml(contractName)}</a>`,
    };

    // Generate subject line
    const subject = `Information Request: ${contractName}`;

    const updatedInvitations: EmailInvitation[] = [];

    // Send emails individually to track success/failure per recipient
    for (const invitation of invitations) {
      if (invitation.status !== 'pending') {
        updatedInvitations.push(invitation);
        continue;
      }

      // Use custom message or default template
      const emailContent =
        invitation.personalized_message?.trim() ||
        this.getDefaultInvitationTemplate();

      // Add recipient-specific variables
      const recipientVariables = {
        ...templateVariables,
        recipient_name: invitation.email.split('@')[0], // Basic name fallback
      };

      const recipient: EmailRecipient = {
        email: invitation.email,
        name: recipientVariables.recipient_name,
      };

      // Send the email
      const result = await this.sendSingleEmail(
        recipient,
        subject,
        emailContent,
        recipientVariables,
        fromEmail,
        replyToEmail,
      );

      const sentAt = result.success ? new Date().toISOString() : undefined;
      let persistenceError: string | undefined;

      try {
        await createContractEmailInvitation({
          contractId,
          email: invitation.email,
          emailContent,
          url: publicUrl,
          emailSubject: subject,
          contractName,
          status: result.success ? 'sent' : 'failed',
          sentAt,
        });
      } catch (error) {
        persistenceError = getErrorMessage(
          error,
          'Failed to persist invitation record',
        );

        logger.error('Failed to persist contract invitation record', {
          contractId,
          email: invitation.email,
          error: persistenceError,
        });
      }

      // Update invitation status based on result
      const updatedInvitation: EmailInvitation = {
        ...invitation,
        status: result.success && !persistenceError ? 'sent' : 'error',
        sent_at: result.success && !persistenceError ? sentAt : undefined,
        resend_id: result.resend_id,
        error_message: persistenceError || result.error,
      };

      updatedInvitations.push(updatedInvitation);

      // Add small delay between sends to avoid rate limiting
      if (result.success) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    return updatedInvitations;
  }

  /**
   * Validate email configuration
   */
  validateConfiguration(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check if Supabase is configured
    if (!supabase) {
      errors.push('Supabase client not configured');
    }

    // Note: API key validation now happens server-side in Edge Function
    // This method now primarily validates client-side configuration

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Test email functionality with a simple test email
   */
  async testEmailConnection(testEmail: string): Promise<EmailSendResult> {
    const testRecipient: EmailRecipient = {
      email: testEmail,
      name: 'Test User',
    };

    const testVariables: EmailTemplateVariables = {
      contract_name: 'Test Contract',
      company_name: 'Test Company',
      public_url: 'https://example.com/test',
      contract_link: '<a href="https://example.com/test">Test Contract</a>',
    };

    return this.sendSingleEmail(
      testRecipient,
      'Email Service Test',
      'This is a test email to verify that the email service is working correctly.\n\nTest variables:\n- Contract: {{contract_name}}\n- Company: {{company_name}}\n- URL: {{public_url}}',
      testVariables,
      `noreply@${testEmail.split('@')[1]}`, // Use same domain for testing
    );
  }
}

// Export singleton instance
export const emailService = new EmailService();
